import type { CSSProperties } from "react";
import { motion } from "framer-motion";
import SVG from "react-inlinesvg";
import EntityImageThumbnail from "./EntityImageThumbnail";
import { RemoveImageButton } from "./TileActionsMenu";
import type { EntityImageListItem } from "./lib/types";

const withArticle = (noun: string) => `${/^[aeiou]/i.test(noun) ? "an" : "a"} ${noun}`;

const TILE_FADE_IN = { initial: { opacity: 0 }, animate: { opacity: 1 } };

export const MAX_VISIBLE_ALTERNATES = 7;
// The alternates area is always a 4-column x 2-row grid next to the cover, regardless of how many
// alternates actually load — the skeleton mirrors that fixed footprint.
const SKELETON_TILE_COUNT = 8;

interface EntityImageGalleryPreviewProps {
  images: EntityImageListItem[];
  totalAlternateCount: number;
  isLoading: boolean;
  coverImageUrl?: string | null;
  coverImageFailed: boolean;
  onCoverImageError: () => void;
  isGenerating: boolean;
  readOnly: boolean;
  onCoverClick: () => void;
  onAddClick: () => void;
  onAlternateClick: (image: EntityImageListItem) => void;
  onOverflowClick: () => void;
  onRemoveImage?: (imageId: string) => Promise<void>;
  entityDisplayName: string;
  /** Singular noun for the images, e.g. "portrait" or "image". */
  imageNoun?: string;
  maxVisibleAlternates?: number;
}

export default function EntityImageGalleryPreview({
  images,
  totalAlternateCount,
  isLoading,
  coverImageUrl,
  coverImageFailed,
  onCoverImageError,
  isGenerating,
  readOnly,
  onCoverClick,
  onAddClick,
  onAlternateClick,
  onOverflowClick,
  onRemoveImage,
  entityDisplayName,
  imageNoun = "portrait",
  maxVisibleAlternates = MAX_VISIBLE_ALTERNATES,
}: EntityImageGalleryPreviewProps) {
  const alternates = images.filter((image) => image.status === "ALTERNATE");
  const visibleAlternates = alternates.slice(0, maxVisibleAlternates);
  // hasOverflow/hiddenCount use the true server-side total, not alternates.length — images only
  // reflects however many pages have been fetched so far, and the gallery page
  // size is kept >= maxVisibleAlternates so the first page always has enough loaded alternates to
  // fill the visible slots below.
  const hasOverflow = totalAlternateCount > maxVisibleAlternates;
  const hiddenCount = totalAlternateCount - visibleAlternates.length;
  // With no cover and no alternates, the empty cover tile is itself the "add an image" CTA —
  // a second add tile next to it would be redundant.
  const hasNoPortraits = !isLoading && !coverImageUrl && totalAlternateCount === 0;

  // Nothing to show yet: render a single small, neutral tile rather than the full-width 2x2
  // cover block, so the empty state stays out of the way on a card the user may not be adding
  // an image to.
  if (hasNoPortraits) {
    return (
      <EntityImageThumbnail
        size="md"
        className="!h-32 !w-32"
        wrapperClassName="block"
        imageUrl={null}
        imageFailed={false}
        onImageError={onCoverImageError}
        isGenerating={isGenerating}
        readOnly={readOnly}
        onClick={onCoverClick}
        ariaLabel={`Add ${withArticle(imageNoun)} for ${entityDisplayName}`}
        tooltipText={`Add ${withArticle(imageNoun)}`}
        tooltipDisabled={readOnly}
      />
    );
  }

  const coverImageId = images.find((image) => image.status === "COVER")?.id;
  const canRemoveCover = !readOnly && !!onRemoveImage && !!coverImageId && !!coverImageUrl && !coverImageFailed;

  return (
    <div className="grid w-full grid-cols-6 grid-rows-[auto_auto] grid-flow-col gap-[10px]">
      <div className="group relative col-span-2 row-span-2">
        <EntityImageThumbnail
          size="lg"
          className="entity-image--fluid"
          wrapperClassName="w-full"
          imageUrl={coverImageUrl}
          imageFailed={coverImageFailed}
          onImageError={onCoverImageError}
          isGenerating={isGenerating}
          readOnly={readOnly}
          onClick={onCoverClick}
          ariaLabel={`${entityDisplayName}'s ${imageNoun}${isGenerating ? " (generating)" : ""}`}
          tooltipText={readOnly ? `No ${imageNoun} yet` : `Add ${withArticle(imageNoun)}`}
          tooltipDisabled={!!coverImageUrl && !coverImageFailed}
        />
        {canRemoveCover && (
          <RemoveImageButton
            onRemove={() => onRemoveImage(coverImageId)}
            className="right-2 top-2"
          />
        )}
      </div>
      {isLoading &&
        Array.from({ length: SKELETON_TILE_COUNT }, (_, i) => (
          <SkeletonTile
            key={i}
            index={i}
          />
        ))}
      {!isLoading &&
        visibleAlternates.map((image, i) => (
          <motion.div
            key={image.id}
            {...TILE_FADE_IN}
            transition={{ duration: 0.25, delay: i * 0.04 }}
            className="group relative"
          >
            <EntityImageThumbnail
              size="sm"
              className="entity-image--fluid"
              wrapperClassName="w-full"
              imageUrl={image.url}
              imageFailed={false}
              onImageError={() => {}}
              isGenerating={false}
              readOnly={readOnly}
              onClick={() => onAlternateClick(image)}
              ariaLabel={`${entityDisplayName}'s ${imageNoun}`}
              tooltipText={`View ${imageNoun}`}
              tooltipDisabled={true}
            />
            {!readOnly && onRemoveImage && <RemoveImageButton onRemove={() => onRemoveImage(image.id)} />}
          </motion.div>
        ))}
      {!isLoading && (hasOverflow || !readOnly) && (
        <motion.div
          {...TILE_FADE_IN}
          transition={{ duration: 0.25, delay: visibleAlternates.length * 0.04 }}
        >
          {hasOverflow ? (
            <OverflowTile
              count={hiddenCount}
              imageNoun={imageNoun}
              onClick={onOverflowClick}
              readOnly={readOnly}
            />
          ) : (
            <AddImageTile
              onClick={onAddClick}
              imageNoun={imageNoun}
            />
          )}
        </motion.div>
      )}
    </div>
  );
}

function AddImageTile({ onClick, imageNoun }: { onClick: () => void; imageNoun: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Add ${withArticle(imageNoun)}`}
      className="entity-image entity-image--cta entity-image--fluid use-component-styles flex"
    >
      <SVG
        src="/assets/add.svg"
        width={32}
        height={32}
        aria-hidden="true"
      />
    </button>
  );
}

function SkeletonTile({ index }: { index: number }) {
  return (
    <span
      className="entity-image entity-image--fluid entity-image--skeleton"
      style={{ "--skeleton-delay": `${index * 0.1}s` } as CSSProperties}
      aria-hidden="true"
    />
  );
}

function OverflowTile({ count, onClick, readOnly, imageNoun }: { count: number; onClick: () => void; readOnly: boolean; imageNoun: string }) {
  const label = `${count} more ${imageNoun}${count === 1 ? "" : "s"}`;
  const content = <span className="text-lg font-semibold text-app-grey-1">+{count}</span>;

  return readOnly ? (
    <span
      className="entity-image entity-image--fluid"
      aria-label={label}
    >
      {content}
    </span>
  ) : (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="entity-image entity-image--overflow entity-image--fluid use-component-styles flex"
    >
      {content}
    </button>
  );
}
